import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { FiEye, FiEyeOff, FiUser, FiLock } from "react-icons/fi";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { signIn } from '../api/auth'; // Adjust the import path as needed
import { useNavigate } from "react-router-dom";

const SigninSchema = Yup.object().shape({
    emailOrUsername: Yup.string().required("Email or Username is required"),
    password: Yup.string()
        .min(6, "Password too short")
        .required("Password is required"),
});

const SigninForm = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-200">
            <div className="w-full max-w-md p-6 bg-blue-600 text-white shadow-lg rounded-lg">
                <h1 className="text-center text-2xl font-bold mb-2">Sign In</h1>
                <p className="text-center text-white mb-6">Welcome Back!</p>

                <Formik
                    initialValues={{
                        emailOrUsername: "",
                        password: "",
                    }}
                    validationSchema={SigninSchema}
                    onSubmit={async (values, { setSubmitting, resetForm }) => {
                        try {
                            const response = await signIn(values);
                            const username = response.username;
                            if(response.status === "success"){
                                navigate(`/profile/${encodeURIComponent(username)}`);
                                resetForm();
                            }
                            //console.log("Sign-In Response:", response);
                        } catch (error) {
                            //console.error("Sign-In Error:", error);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            {/* Email or Username Input */}
                            <div className="mb-4 relative">
                                <Field
                                    type="text"
                                    name="emailOrUsername"
                                    placeholder="Enter Email or Username"
                                    className="w-full p-3 pl-10 bg-white text-gray-900 border border-gray-300 rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <FiUser className="absolute left-3 top-3 text-gray-400" />
                                <ErrorMessage
                                    name="emailOrUsername"
                                    component="div"
                                    className="text-sm text-red-500 mt-1"
                                />
                            </div>

                            {/* Password Input */}
                            <div className="relative mb-4">
                                <Field
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="Password"
                                    className="w-full p-3 pl-10 bg-white text-gray-900 border border-gray-300 rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <FiLock className="absolute left-3 top-3 text-gray-400" />
                                <div
                                    className="absolute right-3 top-3 cursor-pointer text-gray-400"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                >
                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                </div>
                                <ErrorMessage
                                    name="password"
                                    component="div"
                                    className="text-sm text-red-500 mt-1"
                                />
                            </div>

                            {/* Remember Me & Forgot Password */}
                            <div className="flex items-center justify-between mb-4">
                                <label className="flex items-center text-sm">
                                    <Field
                                        type="checkbox"
                                        name="rememberMe"
                                        className="w-4 h-4 text-blue-500 bg-gray-200 rounded focus:ring-2 focus:ring-purple-500"
                                    />
                                    <span className="ml-2">Remember Me</span>
                                </label>
                                <Link
                                    to="/ncpc/forgot_password"
                                    className="text-sm text-white underline hover:text-gray-300"
                                >
                                    Forgot Password?
                                </Link>
                            </div>

                            {/* Sign-In Button */}
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full py-3 rounded-lg font-semibold transition duration-300 ${
                                    isSubmitting
                                        ? "bg-blue-700 cursor-not-allowed"
                                        : "bg-blue-400 hover:bg-blue-700"
                                }`}
                            >
                                {isSubmitting ? "Signing In..." : "Sign In"}
                            </button>
                            
                            {/* Footer Link */}
                            <p className="text-center text-sm text-white">
                                Don't have an account?{" "}
                                <Link
                                    to="/sign_up"
                                    className="font-semibold underline hover:text-gray-300"
                                >
                                    Sign Up Here
                                </Link>
                            </p>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default SigninForm;
